
import { useState } from 'react';
import { TrackDownload } from '@/lib/types';
import { useToast } from '@/components/ui/use-toast';
import { isValidSpotifyUrl } from '@/lib/spotify';
import { updateTrack } from '@/hooks/track-upload/utils/databaseService';

export function useTrackCard(
  track: TrackDownload,
  onSaveEdit: (track: TrackDownload) => Promise<void>
) {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editSpotifyUrl, setEditSpotifyUrl] = useState(track.spotifyUrl);
  const [isEditUrlValid, setIsEditUrlValid] = useState(true);
  const [editFile, setEditFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [currentFileName, setCurrentFileName] = useState<string | null>(track.fileName);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editTrackTitle, setEditTrackTitle] = useState(track.trackName || '');
  const [editDescription, setEditDescription] = useState(track.description || '');

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleEditClick = async () => {
    if (isEditOpen) {
      await handleSaveEdit();
    } else {
      setIsEditing(true);
      setEditSpotifyUrl(track.spotifyUrl);
      setIsEditUrlValid(true);
      setEditFile(null);
      setCurrentFileName(track.fileName);
      setEditTrackTitle(track.trackName || '');
      setEditDescription(track.description || '');
      setIsEditOpen(true);
    }
  };

  const handleSpotifyUrlChange = (url: string) => {
    setEditSpotifyUrl(url);
    setIsEditUrlValid(isValidSpotifyUrl(url));
  };

  const handleFileChange = (file: File | null) => {
    setEditFile(file);
  };

  const handleTitleChange = (title: string) => {
    setEditTrackTitle(title);
  };

  const handleDescriptionChange = (description: string) => {
    setEditDescription(description);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setIsEditOpen(false);
    setEditSpotifyUrl(track.spotifyUrl);
    setEditFile(null);
    setEditTrackTitle(track.trackName || '');
    setEditDescription(track.description || '');
  };

  const handleSaveEdit = async () => {
    setIsSaving(true);
    
    try {
      const success = await updateTrack(track.id, {
        spotifyUrl: editSpotifyUrl,
        trackName: editTrackTitle,
        description: editDescription,
        file: editFile,
        artistId: track.artistId
      });
      
      if (success) {
        // Create an updated track object to return to the parent component
        const updatedTrack: TrackDownload = {
          ...track,
          spotifyUrl: editSpotifyUrl,
          trackName: editTrackTitle,
          description: editDescription,
          // Update file details if a new file was uploaded
          ...(editFile && {
            fileName: editFile.name,
            fileSize: editFile.size,
            fileType: editFile.name.split('.').pop() || track.fileType
          })
        };
        
        // Send the updated track to parent component to update state
        await onSaveEdit(updatedTrack);
        setIsEditing(false);
        setIsEditOpen(false);
        
        toast({
          title: "Changes Saved",
          description: "Your track has been updated successfully.",
        });
      } else {
        throw new Error("Failed to update track");
      }
    } catch (error) {
      toast({
        title: "Error Saving Changes",
        description: "There was a problem updating your track.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    isEditing,
    editSpotifyUrl,
    isEditUrlValid,
    editFile,
    isSaving,
    currentFileName,
    isEditOpen,
    editTrackTitle,
    editDescription,
    formatDate,
    handleEditClick,
    handleSpotifyUrlChange,
    handleFileChange,
    handleTitleChange,
    handleDescriptionChange,
    handleCancelEdit,
    handleSaveEdit,
    setIsEditOpen
  };
}
